import axios from 'axios'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SendHorizonalIcon } from 'lucide-react'
import { Button } from '../../../../components'
import { ChatMessageFile } from '../ChatMessageFile'
import clsx from 'clsx'
import { useCogfy, useToasts } from '../../../../hooks'
import { UUID } from '@indigohive/cogfy-types'
import { useMutation } from '@tanstack/react-query'
import { ChatAudioInput, ChatFileInput, ChatTextInput } from './components'
import { v4 as uuid } from 'uuid'

export type ChatInputProps = {
  chatId: UUID
  sendMessageDisabled?: boolean
  onSendChatMessage?: (content: string, files?: { id: UUID, file: File }[]) => void
}

export function ChatInput (props: ChatInputProps) {
  const { chatId, sendMessageDisabled, onSendChatMessage } = props

  const ref = useRef<HTMLTextAreaElement>(null)

  const [value, setValue] = useState('')
  const [files, setFiles] = useState<{ id: UUID, file: File }[]>([])
  const { t } = useTranslation()
  const toast = useToasts()
  const cogfy = useCogfy()

  const sendChatMessageFile = useMutation({
    mutationFn: async () => {
      const uploadResult = await cogfy.uploadChatMessageFiles({
        chatId,
        files: files.map(file => ({
          id: file.id,
          type: file.file.type,
          size: file.file.size,
          name: file.file.name
        }))
      })

      for (const [index, upload] of uploadResult.files.entries()) {
        const signedUrl = upload.signedUrl
        const body = { ...upload.fields, file: files[index].file }
        const headers = { 'Content-Type': 'multipart/form-data' }

        await axios.post(signedUrl, body, { headers })
      }

      await cogfy.completeUploadChatMessageFiles({ fileIds: uploadResult.files.map(file => file.id) })

      onSendChatMessage?.(value, files)
    },
    onError: () => toast.error(t('Error on uploading document')),
    onSuccess: async () => {
      setFiles([])
      setValue('')
    }
  })

  const handleSubmit = (event: React.FormEvent<HTMLFormElement> | React.KeyboardEvent<HTMLTextAreaElement>) => {
    event.preventDefault()

    if (submitDisabled || submitLoading) {
      return
    }

    if (files.length === 0) {
      setValue('')
      onSendChatMessage?.(value)
    } else {
      sendChatMessageFile.mutate()
    }
  }

  useEffect(() => {
    const handler = () => {
      if (ref.current) {
        ref.current.rows = ref.current.value.split('\n').length
      }
    }

    ref.current?.addEventListener('keyup', handler)

    return () => {
      ref.current?.removeEventListener('keyup', handler)
    }
  }, [ref.current])

  const submitDisabled = (!value.trim() && files.length === 0) || sendMessageDisabled
  const submitLoading = sendChatMessageFile.isPending

  return (
    <div className="flex flex-col w-full max-w-screen-md">
      {files.length > 0 && (
        <div className="overflow-x-auto max-w-screen-md">
          <div className="flex flex-row w-full my-2 px-2 gap-2">
            {files.map(file => (
              <ChatMessageFile
                key={file.id}
                file={{ id: file.id, name: file.file.name, size: file.file.size, type: file.file.type }}
                onDelete={() => setFiles(files.filter(_file => _file.id !== file.id))}
              />
            ))}
          </div>
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="flex items-center w-full rounded-[30px] p-1 border border-neutral-content max-w-screen-md bg-base-100 max-h-[25dvh]"
      >
        <ChatAudioInput
          onSendAudio={file => setFiles([{ id: uuid() as UUID, file }])}
        />
        <ChatFileInput
          files={files}
          onFilesChange={files => setFiles(files)}
        />
        <ChatTextInput
          ref={ref}
          value={value}
          onChange={setValue}
          onSubmit={event => handleSubmit(event)}
        />
        <Button
          type="submit"
          className="self-end"
          disabled={submitDisabled}
          circle
          ghost
          color="primary"
        >
          {submitLoading && <div className="loading loading-spinner loading-md" />}
          {!submitLoading && (
            <SendHorizonalIcon
              size={24}
              className={clsx(submitDisabled ? 'text-neutral-content' : 'text-primary')}
            />
          )}
        </Button>
      </form>
    </div>
  )
}
